import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import AdminRoute from '../../components/AdminRoute';
import SettingsLayout from './SettingsLayout';
import SettingsHome from './SettingsHome';
import SettingsSection from './SettingsSection';

// Lazies at module scope: the old accordion created them inside render,
// remounting every section on each render.
const AdminInfo = React.lazy(() => import('../AdminInfo/AdminInfo'));
const ColorConfig = React.lazy(() => import('../ColorConfig/ColorConfig'));
const EmailConfig = React.lazy(() => import('../EmailConfigScreen/EmailConfig'));
const DataInfo = React.lazy(() => import('../DataInfo/DataInfo'));
const UsersList = React.lazy(() => import('../Users/UsersList'));
const AdminHistoryLog = React.lazy(() => import('../AdminHistoryLog/AdminHistoryLog'));
const TabConfiguration = React.lazy(() => import('../../components/TabConfiguration/TabConfiguration.jsx'));
const DebtConceptManagerPage = React.lazy(() => import('./ConceptManagerPages').then((m) => ({ default: m.DebtConceptManagerPage })));
const CreditConceptManagerPage = React.lazy(() => import('./ConceptManagerPages').then((m) => ({ default: m.CreditConceptManagerPage })));
const NewDebtConceptPage = React.lazy(() => import('./ConceptEditorPages').then((m) => ({ default: m.NewDebtConceptPage })));
const EditDebtConceptPage = React.lazy(() => import('./ConceptEditorPages').then((m) => ({ default: m.EditDebtConceptPage })));
const NewCreditConceptPage = React.lazy(() => import('./ConceptEditorPages').then((m) => ({ default: m.NewCreditConceptPage })));
const EditCreditConceptPage = React.lazy(() => import('./ConceptEditorPages').then((m) => ({ default: m.EditCreditConceptPage })));
const DashboardConfiguration = React.lazy(() => import('../../components/DashboardConfiguration/DashboardConfiguration.jsx'));
const DuplicateDuesCleanup = React.lazy(() => import('../../components/DuplicateDuesCleanup/DuplicateDuesCleanup.jsx'));
const TerminologyConfiguration = React.lazy(() => import('../../components/TerminologyConfiguration/TerminologyConfiguration.jsx'));
const PresetConfiguration = React.lazy(() => import('../../components/PresetConfiguration/PresetConfiguration.jsx'));
const GoogleCalendarConfiguration = React.lazy(() => import('../../components/GoogleCalendarConfiguration/GoogleCalendarConfiguration.jsx'));
const ContractTemplateList = React.lazy(() => import('../ContractTemplates/ContractTemplateList.jsx'));
const EsignatureConfiguration = React.lazy(() => import('../../components/EsignatureConfiguration/EsignatureConfiguration.jsx'));
const FacturacionConfiguration = React.lazy(() => import('../../components/FacturacionConfiguration/FacturacionConfiguration.jsx'));

/**
 * Route subtree for the settings area. Rendered as a direct child of
 * <Routes> in App.js. Paths mirror settingsNav.js.
 */
export const getSettingsRoutes = () => (
	<Route path='/AdminScreen/Config' element={<SettingsLayout />}>
		<Route index element={<SettingsHome />} />
		<Route
			path='empresa/info'
			element={
				<SettingsSection titleKey='settings.item.companyInfo'>
					<AdminInfo />
				</SettingsSection>
			}
		/>
		<Route
			path='empresa/colores'
			element={
				<SettingsSection titleKey='settings.item.colors'>
					<ColorConfig />
				</SettingsSection>
			}
		/>
		<Route
			path='empresa/emails'
			element={
				<SettingsSection titleKey='settings.item.emails'>
					<EmailConfig />
				</SettingsSection>
			}
		/>
		<Route
			path='negocio/preset'
			element={
				<SettingsSection titleKey='settings.item.preset'>
					<PresetConfiguration />
				</SettingsSection>
			}
		/>
		<Route
			path='negocio/conceptos-deuda'
			element={
				<SettingsSection
					titleKey='title.debtConceptConfig'
					descriptionKey='title.debtConceptConfigDescription'
				>
					<DebtConceptManagerPage />
				</SettingsSection>
			}
		/>
		<Route
			path='negocio/conceptos-deuda/nuevo'
			element={
				<SettingsSection titleKey='concept.newConcept'>
					<NewDebtConceptPage />
				</SettingsSection>
			}
		/>
		<Route
			path='negocio/conceptos-deuda/:id/editar'
			element={
				<SettingsSection titleKey='concept.editTitle'>
					<EditDebtConceptPage />
				</SettingsSection>
			}
		/>
		<Route
			path='negocio/conceptos-pasividad'
			element={
				<SettingsSection
					titleKey='title.creditConceptConfig'
					descriptionKey='title.creditConceptConfigDescription'
				>
					<CreditConceptManagerPage />
				</SettingsSection>
			}
		/>
		<Route
			path='negocio/conceptos-pasividad/nuevo'
			element={
				<SettingsSection titleKey='concept.newConcept'>
					<NewCreditConceptPage />
				</SettingsSection>
			}
		/>
		<Route
			path='negocio/conceptos-pasividad/:id/editar'
			element={
				<SettingsSection titleKey='concept.editTitle'>
					<EditCreditConceptPage />
				</SettingsSection>
			}
		/>
		{/* Old single terminology page moved to per-category pages */}
		<Route path='negocio/terminologia' element={<Navigate to='../terminologia/conceptos' replace />} />
		{/* No descriptionKey here: TerminologyConfiguration renders its own
		    "empty fields revert" intro on every terminology page — a second
		    line would stack two descriptions under the title. */}
		<Route
			path='terminologia/conceptos'
			element={
				<SettingsSection titleKey='settings.item.termEntities'>
					<TerminologyConfiguration prefixes={['entity', 'nav']} />
				</SettingsSection>
			}
		/>
		<Route
			path='terminologia/titulos'
			element={
				<SettingsSection titleKey='settings.item.termTitles'>
					<TerminologyConfiguration prefixes={['title']} />
				</SettingsSection>
			}
		/>
		<Route
			path='terminologia/campos'
			element={
				<SettingsSection titleKey='settings.item.termFields'>
					<TerminologyConfiguration prefixes={['field']} />
				</SettingsSection>
			}
		/>
		<Route
			path='terminologia/formularios'
			element={
				<SettingsSection titleKey='settings.item.termForms'>
					<TerminologyConfiguration prefixes={['form']} />
				</SettingsSection>
			}
		/>
		<Route
			path='terminologia/botones'
			element={
				<SettingsSection titleKey='settings.item.termActions'>
					<TerminologyConfiguration prefixes={['action', 'enum']} />
				</SettingsSection>
			}
		/>
		<Route
			path='terminologia/mensajes'
			element={
				<SettingsSection titleKey='settings.item.termMessages'>
					<TerminologyConfiguration prefixes={['msg']} />
				</SettingsSection>
			}
		/>
		<Route
			path='terminologia/panel'
			element={
				<SettingsSection titleKey='settings.item.termAdmin'>
					<TerminologyConfiguration prefixes={['settings', 'concept']} />
				</SettingsSection>
			}
		/>
		<Route
			path='negocio/tabs'
			element={
				<SettingsSection titleKey='title.tabConfig' descriptionKey='title.tabConfigDescription'>
					<TabConfiguration />
				</SettingsSection>
			}
		/>
		<Route
			path='negocio/dashboard'
			element={
				<SettingsSection titleKey='settings.item.dashboard'>
					<DashboardConfiguration />
				</SettingsSection>
			}
		/>
		<Route
			path='contabilidad/info'
			element={
				<SettingsSection titleKey='title.accountingInfo'>
					<DataInfo />
				</SettingsSection>
			}
		/>
		<Route
			path='usuarios'
			element={
				<AdminRoute>
					<SettingsSection titleKey='settings.item.users'>
						<UsersList />
					</SettingsSection>
				</AdminRoute>
			}
		/>
		<Route
			path='integraciones/calendar'
			element={
				<SettingsSection titleKey='settings.item.googleCalendar'>
					<GoogleCalendarConfiguration />
				</SettingsSection>
			}
		/>
		<Route
			path='integraciones/firma'
			element={
				<SettingsSection titleKey='settings.item.esignature'>
					<EsignatureConfiguration />
				</SettingsSection>
			}
		/>
		<Route
			path='integraciones/facturacion'
			element={
				<SettingsSection titleKey='settings.item.facturacion'>
					<FacturacionConfiguration />
				</SettingsSection>
			}
		/>
		<Route
			path='herramientas/limpieza-duplicados'
			element={
				<SettingsSection
					titleKey='settings.item.duplicateCleanup'
					warningKey='settings.cleanup.warning'
				>
					<DuplicateDuesCleanup />
				</SettingsSection>
			}
		/>
		<Route
			path='herramientas/auditoria'
			element={
				<SettingsSection titleKey='settings.item.auditTracking'>
					<AdminHistoryLog />
				</SettingsSection>
			}
		/>
		<Route
			path='herramientas/plantillas'
			element={
				<SettingsSection titleKey='title.contractTemplates'>
					<ContractTemplateList />
				</SettingsSection>
			}
		/>
		<Route path='*' element={<Navigate to='/AdminScreen/Config' replace />} />
	</Route>
);
